import React from "react";

export interface PlayerControlsProps {
  isPlaying: boolean;
  currentTimeSeconds: number;
  durationSeconds: number;
  currentNodeIndex: number;
  totalNodes: number;
  nodeTitle?: string;
  onPlay: () => void;
  onPause: () => void;
  onSeek: (seconds: number) => void;
}

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export const PlayerControls: React.FC<PlayerControlsProps> = ({
  isPlaying,
  currentTimeSeconds,
  durationSeconds,
  currentNodeIndex,
  totalNodes,
  nodeTitle,
  onPlay,
  onPause,
  onSeek,
}) => {
  const percent = durationSeconds > 0 ? (currentTimeSeconds / durationSeconds) * 100 : 0;
  const progressRef = React.useRef<HTMLDivElement>(null);

  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const bar = progressRef.current;
    if (!bar || durationSeconds <= 0) return;
    const rect = bar.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    onSeek(ratio * durationSeconds);
  };

  const handleProgressDrag = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.buttons !== 1) return;
    handleProgressClick(e);
  };

  return (
    <div className="player-controls">
      {/* Progress bar */}
      <div
        className="progress-bar"
        ref={progressRef}
        onClick={handleProgressClick}
        onMouseMove={handleProgressDrag}
        role="slider"
        aria-label="Playback progress"
        aria-valuenow={Math.round(percent)}
        aria-valuemin={0}
        aria-valuemax={100}
        tabIndex={0}
      >
        <div className="progress-track">
          <div className="progress-fill" style={{ width: `${percent}%` }} />
          <div className="progress-thumb" style={{ left: `${percent}%` }} />
        </div>
      </div>

      {/* Controls row */}
      <div className="controls-row">
        <div className="controls-left">
          {/* Play/Pause button */}
          <button
            className="control-btn play-btn"
            onClick={isPlaying ? onPause : onPlay}
            aria-label={isPlaying ? "Pause" : "Play"}
          >
            {isPlaying ? (
              <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                <rect x="4" y="3" width="4" height="14" rx="1" />
                <rect x="12" y="3" width="4" height="14" rx="1" />
              </svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                <path d="M5 3.5L16.5 10L5 16.5V3.5Z" />
              </svg>
            )}
          </button>

          {/* Time display */}
          <span className="time-display">
            {formatTime(currentTimeSeconds)} / {formatTime(durationSeconds)}
          </span>
        </div>

        <div className="controls-center">
          {nodeTitle && <span className="node-title">{nodeTitle}</span>}
        </div>

        <div className="controls-right">
          {/* Step indicator */}
          <div className="step-indicator">
            {Array.from({ length: totalNodes }, (_, i) => (
              <div
                key={i}
                className={`step-dot ${i === currentNodeIndex ? "active" : ""} ${i < currentNodeIndex ? "completed" : ""}`}
                title={`Step ${i + 1}`}
              />
            ))}
          </div>

          {/* Reset button */}
          <button className="control-btn reset-btn" onClick={() => onSeek(0)} aria-label="Reset" title="Reset">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <path d="M2 2v5h5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M3.05 9A6 6 0 1 0 4.34 4.34L2 7" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};
